/*
 * SkylitWheel — the one scroll-wheel column behind every date/time picker
 * (star map, moon, family moon). Each picker keeps its own modal, hidden
 * inputs and apply logic; this file owns everything that happens inside a
 * wheel column:
 *
 *   - live selection: the value under the centre band is bold the moment it
 *     arrives, not after the wheel stops
 *   - drum look: rows tilt away and fade with distance from the centre
 *   - taps glide to the tapped value instead of jumping
 *   - mouse wheel / trackpad stepping (one row per notch, trackpads by
 *     distance), arrow keys, Page Up/Down, Home/End, type-ahead
 *   - typeable columns (years): tap the centre value to type it
 *   - listbox / option roles, aria-selected, aria-activedescendant
 *   - haptics: Android vibrates per row while dragging; iOS (no Vibration
 *     API, and since iOS 26.5 only a REAL tap can fire the switch haptic)
 *     gets a tick on taps via a display:contents <label> around the column
 *     owning a hidden <input type="checkbox" switch>
 *
 * Geometry comes from the product CSS, which all share it: rows 44px, the
 * column padded (window - row) / 2 top and bottom, scroll-snap centred.
 *
 * Usage:
 *   var w = SkylitWheel.create(columnEl, {
 *     itemClass: 'time-picker-item',        // product CSS class for rows
 *     items: [{ value: 1, label: '1' }, …], // or plain values
 *     value: 1,
 *     label: 'Hour',                         // accessible name
 *     typeable: false,                       // tap centre to type (numbers)
 *     onChange: function (value, index) {},  // fires as the customer moves it
 *     onEnter: function () {}                // Enter key: confirm the picker
 *   });
 *   w.setItems(items, value); w.setValue(value); w.value(); w.sync();
 */
(function () {
  if (window.SkylitWheel) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canVibrate = typeof navigator.vibrate === 'function';
  var isIOS = /iP(hone|ad|od)/.test(navigator.platform) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // ── Haptics ────────────────────────────────────────────────────────────
  var lastTick = 0;
  function tick() {
    if (!canVibrate || reduceMotion) return;
    var now = Date.now();
    if (now - lastTick < 35) return; // fast flings: a tick train, not a buzz
    lastTick = now;
    try { navigator.vibrate(8); } catch (e) { /* noop */ }
  }

  function hiddenSwitch() {
    var sw = document.createElement('input');
    sw.type = 'checkbox';
    sw.setAttribute('switch', '');
    sw.tabIndex = -1;
    sw.setAttribute('aria-hidden', 'true');
    sw.style.cssText = 'position:absolute;width:1px;height:1px;margin:0;padding:0;visibility:hidden;pointer-events:none;';
    // The label re-dispatches the tap as a click on the switch; keep that
    // copy away from the page's click handlers.
    sw.addEventListener('click', function (e) { e.stopPropagation(); });
    return sw;
  }

  // Buttons (Confirm / Done / Today): Android ticks on click; iOS gets a
  // transparent overlay label so a real tap lands on it and fires the haptic
  // (the ios-haptics library pattern). The click still bubbles to the button.
  function hapticButton(btn) {
    if (!btn || btn._skyHaptic) return;
    btn._skyHaptic = true;
    if (canVibrate) btn.addEventListener('click', tick);
    if (!isIOS || reduceMotion) return;
    var label = document.createElement('label');
    label.setAttribute('aria-hidden', 'true');
    label.style.cssText = 'position:absolute;inset:0;touch-action:manipulation;border-radius:inherit;';
    label.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    label.appendChild(hiddenSwitch());
    if (getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
    btn.appendChild(label);
  }

  // ── Shared styles (one tag for all products) ───────────────────────────
  function injectStyles() {
    if (document.getElementById('sky-wheel-styles')) return;
    var css =
      // Vertical only: the drum tilt makes each row's projected box a touch
      // wider than the column, which iOS otherwise lets you drag sideways.
      '.sky-wheel{outline:none;overscroll-behavior:contain;overflow-x:hidden!important;touch-action:pan-y;}' +
      '.sky-wheel:focus-visible{box-shadow:inset 0 0 0 2px rgba(99,62,41,.35);border-radius:8px;}' +
      '.sky-wheel>*{transform-origin:50% 50%;-webkit-backface-visibility:hidden;backface-visibility:hidden;}' +
      '.sky-wheel--typeable>.selected{text-decoration:underline dotted rgba(0,0,0,.35);text-decoration-thickness:1.5px;text-underline-offset:5px;cursor:text;}' +
      '.sky-wheel__type{position:absolute;z-index:5;box-sizing:border-box;height:40px;margin:0;padding:0 4px;' +
      'border:1.5px solid rgba(99,62,41,.45);border-radius:8px;background:#fff;color:#1a1a1a;' +
      'font-family:inherit;font-size:18px;font-weight:600;line-height:1;text-align:center;font-variant-numeric:tabular-nums;' +
      'box-shadow:0 2px 10px rgba(0,0,0,.12);-webkit-appearance:none;appearance:none;}' +
      '.sky-wheel__type:focus{outline:none;border-color:rgba(99,62,41,.8);}';
    var style = document.createElement('style');
    style.id = 'sky-wheel-styles';
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  var uid = 0;
  var ROW_DEG = 19;       // drum curvature per row
  var FADE_PER_ROW = 0.12; // opacity lost per row (the product CSS gradient fades too)
  var MIN_OPACITY = 0.5;

  function Wheel(col, o) {
    this.col = col;
    this.o = o || {};
    this.itemClass = this.o.itemClass || 'sky-wheel-item';
    this.id = 'skw' + (++uid);
    this.items = [];
    this.els = [];
    this.vis = -1;        // index shown as selected
    this.committed = -1;  // index last reported through onChange
    this.target = null;   // index a glide is heading for
    this.driving = 'program';
    this.h = 0; this.pad = 0; this.ch = 0;
    this.raf = 0;
    this.typeBuf = ''; this.typeTimer = 0;
    this.glideTimer = 0;

    injectStyles();
    col.classList.add('sky-wheel');
    if (this.o.typeable) col.classList.add('sky-wheel--typeable');
    col.setAttribute('role', 'listbox');
    if (this.o.label) col.setAttribute('aria-label', this.o.label);
    col.tabIndex = 0;

    if (isIOS && !reduceMotion) this._wrapForHaptics();
    this._bind();
    this.setItems(this.o.items || [], this.o.value);
  }

  Wheel.prototype._wrapForHaptics = function () {
    var col = this.col, parent = col.parentNode;
    if (!parent) return;
    var label = document.createElement('label');
    label.setAttribute('data-sky-haptic', 'wheel');
    label.style.display = 'contents';
    label.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    parent.insertBefore(label, col);
    label.appendChild(col);
    label.appendChild(hiddenSwitch());
  };

  // ── Items ──────────────────────────────────────────────────────────────
  function norm(items) {
    return items.map(function (it) {
      return (it !== null && typeof it === 'object') ? { value: it.value, label: it.label != null ? String(it.label) : String(it.value) }
        : { value: it, label: String(it) };
    });
  }

  Wheel.prototype.setItems = function (items, value) {
    var next = norm(items);
    var same = next.length === this.items.length && next.every(function (it, i) {
      return it.label === this.items[i].label && it.value == this.items[i].value; // eslint-disable-line eqeqeq
    }, this);
    var keep = value !== undefined ? value : (this.vis >= 0 && this.items[this.vis] ? this.items[this.vis].value : undefined);
    var keepIndex = this.vis;
    if (!same) {
      this.items = next;
      var frag = document.createDocumentFragment();
      this.els = next.map(function (it, i) {
        var el = document.createElement('div');
        el.className = this.itemClass;
        el.id = this.id + '-' + i;
        el.setAttribute('role', 'option');
        el.setAttribute('aria-selected', 'false');
        el.dataset.value = it.value;
        el.textContent = it.label;
        frag.appendChild(el);
        return el;
      }, this);
      // Children only: the iOS haptic label sits outside the column.
      while (this.col.firstChild) this.col.removeChild(this.col.firstChild);
      this.col.appendChild(frag);
      this.vis = -1;
    }
    var idx = this._indexOf(keep);
    if (idx < 0) idx = this._nearest(keep);
    if (idx < 0) idx = Math.max(0, Math.min(next.length - 1, keepIndex < 0 ? 0 : keepIndex));
    this.committed = idx;
    this._show(idx);
    this._jump(idx);
  };

  Wheel.prototype._indexOf = function (value) {
    if (value === undefined || value === null) return -1;
    for (var i = 0; i < this.items.length; i++) {
      if (this.items[i].value == value) return i; // eslint-disable-line eqeqeq
    }
    return -1;
  };

  // Numeric values not in the list (a saved year outside the range) land on
  // the closest row instead of the first.
  Wheel.prototype._nearest = function (value) {
    var num = Number(value);
    if (value === undefined || value === null || value === '' || isNaN(num)) return -1;
    var best = -1, bestD = Infinity;
    for (var i = 0; i < this.items.length; i++) {
      var d = Math.abs(Number(this.items[i].value) - num);
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  };

  Wheel.prototype.value = function () {
    var i = this.committed >= 0 ? this.committed : this.vis;
    return this.items[i] ? this.items[i].value : undefined;
  };

  // Programmatic: never reports through onChange (the caller made the change).
  Wheel.prototype.setValue = function (value, opts) {
    var idx = this._indexOf(value);
    if (idx < 0) idx = this._nearest(value);
    if (idx < 0) return;
    this.committed = idx;
    this._show(idx);
    if (opts && opts.animate) { this.driving = 'program'; this._glide(idx); }
    else this._jump(idx);
  };

  // Re-measure and re-centre, e.g. right after the picker becomes visible.
  Wheel.prototype.sync = function () {
    this.h = 0;
    this._jump(this.vis < 0 ? 0 : this.vis);
  };

  // ── Geometry ───────────────────────────────────────────────────────────
  Wheel.prototype._measure = function () {
    var first = this.els[0];
    var ch = this.col.clientHeight;
    if (!first || !ch) return false;
    if (!this.h || this.ch !== ch) {
      this.h = first.offsetHeight;
      this.pad = parseFloat(getComputedStyle(this.col).paddingTop) || 0;
      this.ch = ch;
    }
    return this.h > 0;
  };
  Wheel.prototype._topFor = function (i) {
    return this.pad + i * this.h + this.h / 2 - this.ch / 2;
  };
  Wheel.prototype._pos = function () {
    return (this.col.scrollTop + this.ch / 2 - this.pad - this.h / 2) / this.h;
  };
  Wheel.prototype._clamp = function (i) {
    return Math.max(0, Math.min(this.items.length - 1, i));
  };

  Wheel.prototype._jump = function (i) {
    if (!this.items.length) return;
    this.target = null;
    clearTimeout(this.glideTimer);
    if (!this._measure()) return; // hidden: ResizeObserver re-centres on show
    this.driving = 'program';
    this.col.scrollTop = this._topFor(i);
    this._paint(i);
  };

  Wheel.prototype._glide = function (i) {
    if (!this._measure()) { this._jump(i); return; }
    i = this._clamp(i);
    this.target = i;
    var top = this._topFor(i);
    try { this.col.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' }); }
    catch (e) { this.col.scrollTop = top; }
    this._armBackstop(400);
  };

  // Backstop: commit wherever the glide came to rest if it never lands
  // exactly on target (interrupted, or smooth scroll unsupported). Re-armed on
  // every scroll frame, so it only fires once scrolling has stopped: a long
  // glide (40 years) outlasts any fixed timeout.
  Wheel.prototype._armBackstop = function (ms) {
    var self = this;
    clearTimeout(this.glideTimer);
    this.glideTimer = setTimeout(function () {
      if (self.target === null || !self._measure()) return;
      var at = self._clamp(Math.round(self._pos()));
      self.target = null;
      self._show(at);
      self._commit(at);
    }, ms);
  };

  // ── Rendering ──────────────────────────────────────────────────────────
  Wheel.prototype._show = function (i) {
    if (i === this.vis) return;
    var prev = this.els[this.vis];
    if (prev) { prev.classList.remove('selected'); prev.setAttribute('aria-selected', 'false'); }
    var el = this.els[i];
    if (el) {
      el.classList.add('selected');
      el.setAttribute('aria-selected', 'true');
      this.col.setAttribute('aria-activedescendant', el.id);
    }
    this.vis = i;
  };

  Wheel.prototype._commit = function (i) {
    if (i === this.committed || !this.items[i]) return;
    this.committed = i;
    if (this.o.onChange) this.o.onChange(this.items[i].value, i);
  };

  // Drum: only rows near the centre are visible (5 rows in a 220px window),
  // so only ±4 rows around the current position are touched per frame.
  Wheel.prototype._paint = function (pos) {
    var n = this.els.length;
    var lo = Math.max(0, Math.floor(pos) - 4), hi = Math.min(n - 1, Math.ceil(pos) + 4);
    var last = this._painted || [0, -1];
    // Rows leaving the painted range keep stale transforms; they are off
    // screen, but reset them so a later jump never shows a tilted row.
    for (var j = last[0]; j <= last[1]; j++) {
      if ((j < lo || j > hi) && this.els[j]) { this.els[j].style.transform = ''; this.els[j].style.opacity = ''; }
    }
    for (var i = lo; i <= hi; i++) {
      var d = i - pos;
      var ang = Math.max(-75, Math.min(75, d * ROW_DEG));
      var el = this.els[i];
      el.style.transform = Math.abs(d) < 0.01 ? '' : 'perspective(420px) rotateX(' + (-ang).toFixed(2) + 'deg)';
      el.style.opacity = Math.max(MIN_OPACITY, 1 - Math.abs(d) * FADE_PER_ROW).toFixed(3);
    }
    this._painted = [lo, hi];
  };

  Wheel.prototype._frame = function () {
    this.raf = 0;
    if (!this._measure()) return;
    var pos = this._pos();
    var i = this._clamp(Math.round(pos));
    this._paint(pos);
    if (i !== this.vis) {
      this._show(i);
      if (this.driving === 'user') {
        tick();
        if (this.target === null) this._commit(i); // dragging: report live
      }
    }
    if (this.target !== null) {
      if (Math.abs(pos - this.target) < 0.02) {
        var t = this.target;
        this.target = null;
        clearTimeout(this.glideTimer);
        this._commit(t);
      } else {
        this._armBackstop(200);
      }
    }
  };

  // ── Input ──────────────────────────────────────────────────────────────
  Wheel.prototype._bind = function () {
    var self = this, col = this.col;

    col.addEventListener('scroll', function () {
      if (col.scrollLeft) col.scrollLeft = 0;
      if (!self.raf) self.raf = requestAnimationFrame(function () { self._frame(); });
    }, { passive: true });

    // A finger or mouse on the wheel takes over from any glide in progress.
    function takeOver() {
      self.driving = 'user';
      if (self.target !== null) { self.target = null; clearTimeout(self.glideTimer); }
    }
    col.addEventListener('touchstart', takeOver, { passive: true });
    col.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'touch') takeOver(); }, { passive: true });

    col.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target.closest('.' + self.itemClass) : null;
      if (!el || el.parentNode !== col) return;
      var i = self.els.indexOf(el);
      if (i < 0) return;
      if (i === self.vis && self.o.typeable) {
        e.preventDefault(); // skip the iOS label's switch, keep focus for the input
        self._openTyping();
        return;
      }
      if (!isIOS) tick(); // iOS: the haptic label handles taps
      self.driving = 'program';
      self._show(i);
      self._glide(i);
    });

    // Mouse wheel / trackpad. Trackpads fire dozens of small-delta events per
    // gesture (plus momentum after the fingers lift), so distance is
    // accumulated and converted to rows; discrete mouse notches step one row.
    var acc = 0, lastTs = 0;
    col.addEventListener('wheel', function (e) {
      e.preventDefault();
      if (!self.items.length || !self._measure()) return;
      var now = e.timeStamp || Date.now();
      if (now - lastTs > 250) acc = 0;
      lastTs = now;
      var steps;
      if (e.deltaMode === 1 || e.deltaMode === 2) {
        steps = e.deltaY > 0 ? 1 : -1; acc = 0;
      } else if (typeof e.wheelDeltaY === 'number' && e.wheelDeltaY !== 0 && e.wheelDeltaY % 120 === 0) {
        steps = e.deltaY > 0 ? 1 : -1; acc = 0;
      } else {
        acc += e.deltaY;
        steps = Math.trunc(acc / self.h);
        if (!steps) return;
        acc -= steps * self.h;
        steps = Math.max(-3, Math.min(3, steps));
      }
      self._step(steps);
    }, { passive: false });

    col.addEventListener('keydown', function (e) {
      var n = self.items.length;
      if (!n) return;
      var k = e.key;
      if (k === 'ArrowDown') self._step(1);
      else if (k === 'ArrowUp') self._step(-1);
      else if (k === 'PageDown') self._step(5);
      else if (k === 'PageUp') self._step(-5);
      else if (k === 'Home') self._stepTo(0);
      else if (k === 'End') self._stepTo(n - 1);
      else if (k === 'Enter') { if (self.o.onEnter) { e.preventDefault(); self._flush(); self.o.onEnter(); } return; }
      else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && /\S/.test(k)) { self._typeAhead(k); }
      else return;
      e.preventDefault();
    });

    if (window.ResizeObserver) {
      // Pickers open by un-hiding the modal: re-centre once the column has a
      // size, since everything positioned while hidden measured zero.
      var lastH = 0;
      new ResizeObserver(function () {
        var h = col.clientHeight;
        if (h && h !== lastH) { self.h = 0; self._jump(self.vis < 0 ? 0 : self.vis); }
        lastH = h;
      }).observe(col);
    }
  };

  // Commit a glide that is still in flight (Enter pressed mid-glide).
  Wheel.prototype._flush = function () {
    if (this.target === null) return;
    var t = this.target;
    this.target = null;
    clearTimeout(this.glideTimer);
    this._jump(t);
    this._commit(t);
  };

  Wheel.prototype._step = function (steps) {
    var base = this.target !== null ? this.target : this.vis;
    this._stepTo(base + steps);
  };
  Wheel.prototype._stepTo = function (i) {
    i = this._clamp(i);
    this.driving = 'user';
    if (i === (this.target !== null ? this.target : this.vis)) return;
    this._glide(i);
  };

  // Type-ahead: "M" → March, "19" → 1920s, "1987" → 1987. Buffer resets
  // after a pause.
  Wheel.prototype._typeAhead = function (ch) {
    var self = this;
    clearTimeout(this.typeTimer);
    this.typeBuf += ch.toLowerCase();
    this.typeTimer = setTimeout(function () { self.typeBuf = ''; }, 900);
    var buf = this.typeBuf;
    for (var i = 0; i < this.items.length; i++) {
      if (this.items[i].label.toLowerCase().indexOf(buf) === 0) { this._stepTo(i); return; }
    }
  };

  // Typeable columns: an input over the centre band. Enter or blur commits
  // the nearest available value; Escape backs out.
  Wheel.prototype._openTyping = function () {
    if (this._typing) return;
    var host = this.col.offsetParent;
    var el = this.els[this.vis];
    if (!host || !el) return;
    var self = this;
    var hr = host.getBoundingClientRect(), cr = this.col.getBoundingClientRect(), er = el.getBoundingClientRect();
    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'sky-wheel__type';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('enterkeyhint', 'done');
    input.setAttribute('aria-label', 'Type ' + (this.o.label || 'a value').toLowerCase());
    input.autocomplete = 'off';
    input.maxLength = 4;
    input.value = this.items[this.vis].label;
    var w = Math.min(cr.width - 8, 96);
    input.style.width = w + 'px';
    input.style.left = (cr.left - hr.left + (cr.width - w) / 2) + 'px';
    input.style.top = (er.top - hr.top + (er.height - 40) / 2) + 'px';
    host.appendChild(input);
    this._typing = input;
    input.focus();
    input.select();

    var done = false;
    function finish(apply) {
      if (done) return;
      done = true;
      var raw = input.value.trim();
      if (input.parentNode) input.parentNode.removeChild(input);
      self._typing = null;
      if (apply && /^\d+$/.test(raw)) {
        var best = self._nearest(parseInt(raw, 10));
        if (best >= 0) { self.driving = 'program'; self._show(best); self._glide(best); }
      }
      try { self.col.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    input.addEventListener('keydown', function (e) {
      e.stopPropagation(); // keep Escape/Enter away from the modal's handlers
      if (e.key === 'Enter') { e.preventDefault(); finish(true); }
      else if (e.key === 'Escape') { e.preventDefault(); finish(false); }
    });
    input.addEventListener('input', function () { input.value = input.value.replace(/\D/g, '').slice(0, 4); });
    input.addEventListener('blur', function () { finish(true); });
  };

  window.SkylitWheel = {
    create: function (col, opts) {
      if (col._skyWheel) {
        if (opts && opts.items) col._skyWheel.setItems(opts.items, opts.value);
        return col._skyWheel;
      }
      var w = new Wheel(col, opts);
      col._skyWheel = w;
      return w;
    },
    hapticButton: hapticButton,
    tick: tick
  };
})();
